// Content for the Website Design Pricing page
import { SEO_GEO_FAQS } from './seoGeoData';
import { PAID_MEDIA_FAQS, PAID_MEDIA_PROJECT_TYPES } from './paidMediaData';

export const PRICING_PACKAGES = [
  {
    id: 'launchpad',
    title: 'Launchpad Website',
    ideal: 'New businesses, campaigns, events or smaller organisations',
    priceEx: '£1,750',
    priceInc: '£2,100',
    delivery: '5–7 working days',
    description:
      'A focused and professional online presence for new businesses, campaigns, events or smaller organisations.',
    inclusions: [
      'Up to three core pages',
      'Responsive desktop, tablet and mobile design',
      'Brand styling and visual direction',
      'Contact or enquiry form',
      'Basic on-page SEO setup',
      'Google Analytics integration',
      'Cookie and privacy essentials',
      'Two structured rounds of revisions',
      'Launch support',
    ],
    cta: 'Discuss a Launchpad Website',
  },
  {
    id: 'business',
    title: 'Business Website',
    ideal: 'Established businesses that need to generate enquiries',
    priceEx: '£2,995',
    priceInc: '£3,594',
    delivery: '7–10 working days',
    popular: true,
    description:
      'A complete professional website for established businesses that need to communicate clearly, generate enquiries and strengthen their digital presence.',
    inclusions: [
      'Up to five core pages',
      'Discovery and website planning',
      'Bespoke page structure and creative direction',
      'Responsive desktop, tablet and mobile design',
      'Contact and enquiry forms',
      'Core on-page SEO',
      'Google Analytics and Search Console setup',
      'Speed and technical optimisation',
      'Cookie and privacy essentials',
      'Two structured rounds of revisions',
      'Website launch and handover',
      '30 days of post-launch technical support',
    ],
    cta: 'Discuss a Business Website',
  },
  {
    id: 'business-plus',
    title: 'Business Plus',
    ideal: 'Growing businesses with more content and stronger conversion journeys',
    priceEx: '£4,495',
    priceInc: '£5,394',
    delivery: '10–15 working days',
    description:
      'For growing businesses that require more content, stronger conversion journeys, case studies, service sections or external integrations.',
    inclusions: [
      'Up to ten core pages',
      'Discovery and website strategy',
      'Detailed sitemap and content structure',
      'Bespoke visual direction',
      'Conversion-focused page layouts',
      'Responsive design',
      'Advanced forms or lead-capture journeys',
      'Blog, news, case study or portfolio functionality',
      'Core SEO setup',
      'Analytics and Search Console',
      'Standard third-party integrations',
      'Two structured rounds of revisions',
      'Launch, handover and training',
      '30 days of post-launch technical support',
    ],
    cta: 'Discuss a Business Plus Website',
  },
  {
    id: 'ecommerce',
    title: 'Ecommerce Website',
    ideal: 'Businesses ready to sell online with a smooth buying experience',
    priceEx: '£5,995',
    priceInc: '£7,194',
    delivery: '3–5 weeks',
    description:
      'A professionally designed online store built to showcase products, take secure payments and provide customers with a smooth buying experience.',
    inclusions: [
      'WooCommerce or suitable ecommerce platform',
      'Store structure and customer journey planning',
      'Responsive ecommerce design',
      'Product category and product page templates',
      'Initial payment gateway configuration',
      'Standard shipping configuration',
      'Shopping basket and checkout',
      'Customer account functionality',
      'Essential transactional email setup',
      'Initial product population allowance',
      'Analytics and ecommerce tracking foundations',
      'Core technical SEO',
      'Testing and launch support',
      'Basic store-management training',
    ],
    note: 'Final ecommerce pricing depends on catalogue size, product variations, shipping requirements, subscriptions, feeds and third-party integrations.',
    cta: 'Discuss an Ecommerce Project',
  },
];

export const BESPOKE = {
  heading: 'Need something more ambitious?',
  copy:
    'We also design and develop larger websites, customer portals, booking systems, membership platforms, interactive tools, dashboards and bespoke digital experiences.',
  priceEx: '£12,500',
  priceInc: '£15,000',
  delivery: '6–12+ weeks',
  cta: 'Tell us about your project',
};

export const COMPARISON_ROWS = [
  { name: 'Launchpad Website', ex: 'From £1,750', inc: 'From £2,100', delivery: '5–7 working days' },
  { name: 'Business Website', ex: 'From £2,995', inc: 'From £3,594', delivery: '7–10 working days', popular: true },
  { name: 'Business Plus', ex: 'From £4,495', inc: 'From £5,394', delivery: '10–15 working days' },
  { name: 'Ecommerce Website', ex: 'From £5,995', inc: 'From £7,194', delivery: '3–5 weeks' },
  { name: 'Bespoke Digital Project', ex: 'From £12,500', inc: 'From £15,000', delivery: '6–12+ weeks' },
];

export const EVERY_PROJECT_INCLUDES = [
  { icon: 'Compass', label: 'Discovery and planning' },
  { icon: 'MonitorSmartphone', label: 'Responsive design' },
  { icon: 'Palette', label: 'Professional creative direction' },
  { icon: 'Route', label: 'Clear user journeys' },
  { icon: 'MessageSquare', label: 'Contact and enquiry functionality' },
  { icon: 'Search', label: 'SEO foundations' },
  { icon: 'BarChart3', label: 'Analytics setup' },
  { icon: 'ShieldCheck', label: 'Technical testing' },
  { icon: 'ListChecks', label: 'Structured client revisions' },
  { icon: 'Rocket', label: 'Launch support' },
  { icon: 'KeyRound', label: 'Website handover' },
  { icon: 'LifeBuoy', label: 'Post-launch technical support' },
];

export const OPTIONAL_SERVICES = [
  'Website copywriting',
  'Branding and logo design',
  'Photography and video',
  'Search engine optimisation',
  'Google Ads',
  'Email marketing and automation',
  'CRM integrations',
  'Product data and catalogue population',
  'Booking systems',
  'Membership functionality',
  'Ongoing website management',
  'Social media launch campaigns',
];

export const CARE_ITEMS = [
  'Software and security updates',
  'Website backups',
  'Uptime monitoring',
  'Technical maintenance',
  'Minor content updates',
  'Performance checks',
  'Priority support',
  'Analytics and performance reporting',
  'Continued design and development',
];

export const PROCESS_STEPS_PRICING = [
  { step: '1', title: 'Discovery', copy: 'We learn about your business, audience, objectives and requirements.' },
  { step: '2', title: 'Scope', copy: 'We confirm the website structure, functionality, deliverables, costs and projected timeline.' },
  { step: '3', title: 'Design and build', copy: 'We create the core website experience and develop the first working version.' },
  { step: '4', title: 'Review', copy: 'You review the website and provide consolidated feedback through structured revision rounds.' },
  { step: '5', title: 'Test and launch', copy: 'We complete responsive checks, technical testing and launch preparation.' },
  { step: '6', title: 'Support', copy: 'We help you settle into the new website and can provide ongoing care as required.' },
];

export const TURNAROUND_FACTORS = [
  'Content availability',
  'Client feedback and approval times',
  'Third-party integrations',
  'Product catalogue size',
  'Copywriting requirements',
  'Technical complexity',
  'Existing website migration requirements',
];

export const PRICING_FAQS = [
  {
    q: 'How much does a professional business website cost?',
    a: 'Professional Boost! website projects begin at £1,750 plus VAT. A typical five-page business website begins at £2,995 plus VAT. Final pricing depends on the size, content, functionality and technical requirements of the project.',
  },
  {
    q: 'Are the prices fixed?',
    a: 'The prices shown are starting points. Every project is reviewed and scoped individually before a final proposal is issued.',
  },
  {
    q: 'Is VAT included?',
    a: 'Both excluding-VAT and including-VAT prices are displayed. Formal business quotations will show VAT separately.',
  },
  {
    q: 'How long does a five-page website take?',
    a: 'A typical five-page business website takes approximately seven to ten working days from the point at which all required content, information and access have been received.',
  },
  {
    q: 'Will I be able to update the website myself?',
    a: 'Where appropriate, the website will include a manageable content system. Basic handover or training is included with the main business and ecommerce packages.',
  },
  {
    q: 'Is hosting included?',
    a: 'Hosting, domains, premium software licences and other third-party services are normally quoted separately because requirements vary between projects.',
  },
  {
    q: 'Do you provide website copywriting?',
    a: 'Yes. Boost! can write, edit or restructure website copy as an additional service.',
  },
  {
    q: 'Can you redesign an existing website?',
    a: 'Yes. Existing websites can be redesigned, rebuilt or migrated. The existing platform and content will be reviewed before pricing is confirmed.',
  },
  {
    q: 'Do you provide ecommerce websites?',
    a: 'Yes. Ecommerce website projects begin at £5,995 plus VAT, with the final fee depending on catalogue size, shipping, payment, product and integration requirements.',
  },
  {
    q: 'What happens after the website launches?',
    a: 'The project includes an agreed period of post-launch technical support. Ongoing care, updates, content support and marketing services are also available.',
  },
  ...SEO_GEO_FAQS,
  ...PAID_MEDIA_FAQS,
];

export const PROJECT_TYPES = [
  'New business website',
  'Existing website redesign',
  'Ecommerce website',
  'Landing page',
  'Bespoke digital project',
  'Website support',
  'SEO and organic search',
  'GEO and AI-search visibility',
  'Website plus SEO and GEO',
  'Existing website growth programme',
  ...PAID_MEDIA_PROJECT_TYPES,
  'Not sure yet',
];

export const BUDGET_OPTIONS = [
  '£1,500–£3,000',
  '£3,000–£5,000',
  '£5,000–£10,000',
  '£10,000–£20,000',
  '£20,000+',
  'Not sure yet',
];
